/**
 * router-core
 *
 * Copyright (c) TanStack
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * @license MIT
 */
import { createMemoryHistory, createBrowserHistory } from 'history';
export { createBrowserHistory, createHashHistory, createMemoryHistory } from 'history';
import invariant from 'tiny-invariant';
export { default as invariant } from 'tiny-invariant';

/**
 * This function returns `a` if `b` is deeply equal.
 * If not, it will replace any deeply equal children of `b` with those of `a`.
 * This can be used for structural sharing between JSON values for example.
 */
function replaceEqualDeep(prev, next) {
  if (prev === next) {
    return prev;
  }
  const array = Array.isArray(prev) && Array.isArray(next);
  if (array || isPlainObject(prev) && isPlainObject(next)) {
    const aSize = array ? prev.length : Object.keys(prev).length;
    const bItems = array ? next : Object.keys(next);
    const bSize = bItems.length;
    const copy = array ? [] : {};
    let equalItems = 0;
    for (let i = 0; i < bSize; i++) {
      const key = array ? i : bItems[i];
      copy[key] = replaceEqualDeep(prev[key], next[key]);
      if (copy[key] === prev[key]) {
        equalItems++;
      }
    }
    return aSize === bSize && equalItems === aSize ? prev : copy;
  }
  return next;
}

// Copied from: https://github.com/jonschlinkert/is-plain-object
function isPlainObject(o) {
  if (!hasObjectPrototype(o)) {
    return false;
  }

  // If has modified constructor
  const ctor = o.constructor;
  if (typeof ctor === 'undefined') {
    return true;
  }

  // If has modified prototype
  const prot = ctor.prototype;
  if (!hasObjectPrototype(prot)) {
    return false;
  }

  // If constructor does not have an Object-specific method
  if (!prot.hasOwnProperty('isPrototypeOf')) {
    return false;
  }

  // Most likely a plain Object
  return true;
}
function hasObjectPrototype(o) {
  return Object.prototype.toString.call(o) === '[object Object]';
}
function last(arr) {
  return arr[arr.length - 1];
}
function warning(cond, message) {
  if (cond) {
    if (typeof console !== 'undefined') console.warn(message);
    try {
      throw new Error(message);
    } catch (_unused) {}
  }
  return true;
}
function isFunction(d) {
  return typeof d === 'function';
}
function functionalUpdate(updater, previous) {
  if (isFunction(updater)) {
    return updater(previous);
  }
  return updater;
}
function pick(parent, keys) {
  return keys.reduce((obj, key) => {
    obj[key] = parent[key];
    return obj;
  }, {});
}

function joinPaths(paths) {
  return cleanPath(paths.filter(Boolean).join('/'));
}
function cleanPath(path) {
  // remove double slashes
  return path.replace(/\/{2,}/g, '/');
}
function trimPathLeft(path) {
  return path === '/' ? path : path.replace(/^\/{1,}/, '');
}
function trimPathRight(path) {
  return path === '/' ? path : path.replace(/\/{1,}$/, '');
}
function trimPath(path) {
  return trimPathRight(trimPathLeft(path));
}
function resolvePath(basepath, base, to) {
  base = base.replace(new RegExp("^" + basepath), '/');
  to = to.replace(new RegExp("^" + basepath), '/');
  let baseSegments = parsePathname(base);
  const toSegments = parsePathname(to);
  toSegments.forEach((toSegment, index) => {
    if (toSegment.value === '/') {
      if (!index) {
        // Leading slash
        baseSegments = [toSegment];
      } else if (index === toSegments.length - 1) {
        // Trailing Slash
        baseSegments.push(toSegment);
      } else ;
    } else if (toSegment.value === '..') {
      var _last;
      // Extra trailing slash? pop it off
      if (baseSegments.length > 1 && ((_last = last(baseSegments)) == null ? void 0 : _last.value) === '/') {
        baseSegments.pop();
      }
      baseSegments.pop();
    } else if (toSegment.value === '.') {
      return;
    } else {
      baseSegments.push(toSegment);
    }
  });
  const joined = joinPaths([basepath, ...baseSegments.map(d => d.value)]);
  return cleanPath(joined);
}
function parsePathname(pathname) {
  if (!pathname) {
    return [];
  }
  pathname = cleanPath(pathname);
  const segments = [];
  if (pathname.slice(0, 1) === '/') {
    pathname = pathname.substring(1);
    segments.push({
      type: 'pathname',
      value: '/'
    });
  }
  if (!pathname) {
    return segments;
  }

  // Remove empty segments and '.' segments
  const split = pathname.split('/').filter(Boolean);
  segments.push(...split.map(part => {
    if (part.startsWith('*')) {
      return {
        type: 'wildcard',
        value: part
      };
    }
    if (part.charAt(0) === '$') {
      return {
        type: 'param',
        value: part
      };
    }
    return {
      type: 'pathname',
      value: part
    };
  }));
  if (pathname.slice(-1) === '/') {
    pathname = pathname.substring(1);
    segments.push({
      type: 'pathname',
      value: '/'
    });
  }
  return segments;
}
function interpolatePath(path, params, leaveWildcard) {
  const interpolatedPathSegments = parsePathname(path);
  return joinPaths(interpolatedPathSegments.map(segment => {
    if (segment.value === '*' && !leaveWildcard) {
      return '';
    }
    if (segment.type === 'param') {
      var _segment$value$substr;
      return (_segment$value$substr = params[segment.value.substring(1)]) != null ? _segment$value$substr : '';
    }
    return segment.value;
  }));
}
function matchPathname(basepath, currentPathname, matchLocation) {
  const pathParams = matchByPath(basepath, currentPathname, matchLocation);
  // const searchMatched = matchBySearch(currentLocation.search, matchLocation)

  if (matchLocation.to && !pathParams) {
    return;
  }
  return pathParams != null ? pathParams : {};
}
function matchByPath(basepath, from, matchLocation) {
  var _matchLocation$to;
  if (!from.startsWith(basepath)) {
    return undefined;
  }
  from = basepath != '/' ? from.substring(basepath.length) : from;
  const baseSegments = parsePathname(from);
  const to = "" + ((_matchLocation$to = matchLocation.to) != null ? _matchLocation$to : '*');
  const routeSegments = parsePathname(to);
  const params = {};
  let isMatch = (() => {
    for (let i = 0; i < Math.max(baseSegments.length, routeSegments.length); i++) {
      const baseSegment = baseSegments[i];
      const routeSegment = routeSegments[i];
      const isLastRouteSegment = i === routeSegments.length - 1;
      const isLastBaseSegment = i === baseSegments.length - 1;
      if (routeSegment) {
        if (routeSegment.type === 'wildcard') {
          if (baseSegment != null && baseSegment.value) {
            params['*'] = joinPaths(baseSegments.slice(i).map(d => d.value));
            return true;
          }
          return false;
        }
        if (routeSegment.type === 'pathname') {
          if (routeSegment.value === '/' && !(baseSegment != null && baseSegment.value)) {
            return true;
          }
          if (baseSegment) {
            if (matchLocation.caseSensitive) {
              if (routeSegment.value !== baseSegment.value) {
                return false;
              }
            } else if (routeSegment.value.toLowerCase() !== baseSegment.value.toLowerCase()) {
              return false;
            }
          }
        }
        if (!baseSegment) {
          return false;
        }
        if (routeSegment.type === 'param') {
          if ((baseSegment == null ? void 0 : baseSegment.value) === '/') {
            return false;
          }
          if (baseSegment.value.charAt(0) !== '$') {
            params[routeSegment.value.substring(1)] = baseSegment.value;
          }
        }
      }
      if (isLastRouteSegment && !isLastBaseSegment) {
        return !!matchLocation.fuzzy;
      }
    }
    return true;
  })();
  return isMatch ? params : undefined;
}

// @ts-nocheck

// qss has been slightly modified and inlined here for our use cases (and compression's sake). We've included it as a hard dependency for MIT license attribution.

function encode(obj, pfx) {
  var k,
    i,
    tmp,
    str = '';
  for (k in obj) {
    if ((tmp = obj[k]) !== void 0) {
      if (Array.isArray(tmp)) {
        for (i = 0; i < tmp.length; i++) {
          str && (str += '&');
          str += encodeURIComponent(k) + '=' + encodeURIComponent(tmp[i]);
        }
      } else {
        str && (str += '&');
        str += encodeURIComponent(k) + '=' + encodeURIComponent(tmp);
      }
    }
  }
  return (pfx || '') + str;
}
function toValue(mix) {
  if (!mix) return '';
  var str = decodeURIComponent(mix);
  if (str === 'false') return false;
  if (str === 'true') return true;
  if (str.charAt(0) === '0') return str;
  return +str * 0 === 0 ? +str : str;
}
function decode(str) {
  var tmp,
    k,
    out = {},
    arr = str.split('&');
  while (tmp = arr.shift()) {
    tmp = tmp.split('=');
    k = tmp.shift();
    if (out[k] !== void 0) {
      out[k] = [].concat(out[k], toValue(tmp.shift()));
    } else {
      out[k] = toValue(tmp.shift());
    }
  }
  return out;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

function createRoute(routeConfig, options, parent, router) {
  const {
    id,
    routeId,
    path: routePath,
    fullPath
  } = routeConfig;
  const action = router.state.actions[id] || (() => {
    router.state.actions[id] = {
      submissions: [],
      submit: async (submission, actionOpts) => {
        var _actionOpts$invalidat;
        if (!route) {
          return;
        }
        const invalidate = (_actionOpts$invalidat = actionOpts == null ? void 0 : actionOpts.invalidate) != null ? _actionOpts$invalidat : true;
        if (!(actionOpts != null && actionOpts.multi)) {
          action.submissions = action.submissions.filter(d => d.isMulti);
        }
        const actionState = {
          submittedAt: Date.now(),
          status: 'pending',
          submission,
          isMulti: !!(actionOpts != null && actionOpts.multi)
        };
        action.current = actionState;
        action.latest = actionState;
        action.submissions.push(actionState);
        router.notify();
        try {
          const res = await (route.options.action == null ? void 0 : route.options.action(submission));
          actionState.data = res;
          if (invalidate) {
            router.invalidateRoute({
              to: '.',
              fromCurrent: true
            });
            await router.reload();
          }
          actionState.status = 'success';
          return res;
        } catch (err) {
          console.error(err);
          actionState.error = err;
          actionState.status = 'error';
        } finally {
          router.notify();
        }
      }
    };
    return router.state.actions[id];
  })();
  const loader = router.state.loaders[id] || (() => {
    router.state.loaders[id] = {
      pending: [],
      fetch: async loaderContext => {
        if (!route) {
          return;
        }
        const loaderState = {
          loadedAt: Date.now(),
          loaderContext
        };
        loader.current = loaderState;
        loader.latest = loaderState;
        loader.pending.push(loaderState);

        // router.state = {
        //   ...router.state,
        //   currentAction: loaderState,
        //   latestAction: loaderState,
        // }

        router.notify();
        try {
          return await (route.options.loader == null ? void 0 : route.options.loader(loaderContext));
        } finally {
          loader.pending = loader.pending.filter(d => d !== loaderState);
          // router.removeActionQueue.push({ loader, loaderState })
          router.notify();
        }
      }
    };
    return router.state.loaders[id];
  })();
  let route = {
    routeInfo: undefined,
    routeId: id,
    routeRouteId: routeId,
    routePath,
    fullPath,
    options,
    router,
    childRoutes: undefined,
    parentRoute: parent,
    action,
    loader: loader,
    buildLink: options => {
      return router.buildLink(_extends({}, options, {
        from: fullPath
      }));
    },
    navigate: options => {
      return router.navigate(_extends({}, options, {
        from: fullPath
      }));
    },
    matchRoute: (matchLocation, opts) => {
      return router.matchRoute(_extends({}, matchLocation, {
        from: fullPath
      }), opts);
    }
  };
  router.options.createRoute == null ? void 0 : router.options.createRoute({
    router,
    route
  });
  return route;
}

const rootRouteId = '__root__';
const createRouteConfig = function createRouteConfig(options, children, isRoot, parentId, parentPath) {
  if (options === void 0) {
    options = {};
  }
  if (isRoot === void 0) {
    isRoot = true;
  }
  if (isRoot) {
    options.path = rootRouteId;
  }

  // Strip the root from parentIds
  if (parentId === rootRouteId) {
    parentId = '';
  }
  let path = isRoot ? rootRouteId : options.path;

  // If the path is anything other than an index path, trim it up
  if (path && path !== '/') {
    path = trimPath(path);
  }
  const routeId = path || options.id;
  let id = joinPaths([parentId, routeId]);
  if (path === rootRouteId) {
    path = '/';
  }
  if (id !== rootRouteId) {
    id = joinPaths(['/', id]);
  }
  const fullPath = id === rootRouteId ? '/' : trimPathRight(joinPaths([parentPath, path]));
  return {
    id: id,
    routeId: routeId,
    path: path,
    fullPath: fullPath,
    options: options,
    children,
    addChildren: children => createRouteConfig(options, children, false, parentId, parentPath),
    createRoute: childOptions => createRouteConfig(childOptions, undefined, false, id, fullPath),
    generate: () => {
      invariant(false, "routeConfig.generate() is used by TanStack Router's file-based routing code generation and should not actually be called during runtime. ");
    }
  };
};

const componentTypes = ['component', 'errorComponent', 'pendingComponent'];
function createRouteMatch(router, route, opts) {
  const routeMatch = _extends({}, route, opts, {
    router,
    routeSearch: {},
    search: {},
    childMatches: [],
    status: 'idle',
    routeLoaderData: {},
    loaderData: {},
    isFetching: false,
    isInvalid: false,
    invalidAt: Infinity,
    // pendingActions: [],
    getIsInvalid: () => {
      const now = Date.now();
      return routeMatch.isInvalid || routeMatch.invalidAt < now;
    },
    __: {
      abortController: new AbortController(),
      latestId: '',
      resolve: () => {},
      notify: () => {
        routeMatch.__.resolve();
        routeMatch.router.notify();
      },
      validate: () => {
        var _routeMatch$parentMat, _routeMatch$parentMat2;
        // Validate the search params and stabilize them
        const parentSearch = (_routeMatch$parentMat = (_routeMatch$parentMat2 = routeMatch.parentMatch) == null ? void 0 : _routeMatch$parentMat2.search) != null ? _routeMatch$parentMat : router.state.currentLocation.search;
        try {
          var _validator;
          const prevSearch = routeMatch.routeSearch;
          const validator = typeof routeMatch.options.validateSearch === 'object' ? routeMatch.options.validateSearch.parse : routeMatch.options.validateSearch;
          let nextSearch = replaceEqualDeep(prevSearch, (_validator = validator == null ? void 0 : validator(parentSearch)) != null ? _validator : {});

          // Invalidate route matches when search param stability changes
          if (prevSearch !== nextSearch) {
            routeMatch.isInvalid = true;
          }
          routeMatch.routeSearch = nextSearch;
          routeMatch.search = replaceEqualDeep(parentSearch, _extends({}, parentSearch, nextSearch));
          componentTypes.map(async type => {
            const component = routeMatch.options[type];
            if (typeof routeMatch.__[type] !== 'function') {
              routeMatch.__[type] = component;
            }
          });
        } catch (err) {
          console.error(err);
          const error = new Error('Invalid search params found', {
            cause: err
          });
          error.code = 'INVALID_SEARCH_PARAMS';
          routeMatch.status = 'error';
          routeMatch.error = error;
          // Do not proceed with loading the route
          return;
        }
      }
    },
    cancel: () => {
      var _routeMatch$__$abortC;
      (_routeMatch$__$abortC = routeMatch.__.abortController) == null ? void 0 : _routeMatch$__$abortC.abort();
    },
    invalidate: () => {
      routeMatch.isInvalid = true;
    },
    hasLoaders: () => {
      return !!(route.options.loader || componentTypes.some(d => {
        var _route$options$d;
        return (_route$options$d = route.options[d]) == null ? void 0 : _route$options$d.preload;
      }));
    },
    load: async loaderOpts => {
      const now = Date.now();
      const minMaxAge = loaderOpts != null && loaderOpts.preload ? Math.max(loaderOpts == null ? void 0 : loaderOpts.maxAge, loaderOpts == null ? void 0 : loaderOpts.gcMaxAge) : 0;

      // If this is a preload, add it to the preload cache
      if (loaderOpts != null && loaderOpts.preload && minMaxAge > 0) {
        // If the match is currently active, don't preload it
        if (router.state.currentMatches.find(d => d.matchId === routeMatch.matchId)) {
          return;
        }
        router.matchCache[routeMatch.matchId] = {
          gc: now + loaderOpts.gcMaxAge,
          match: routeMatch
        };
      }

      // If the match is invalid, errored or idle, trigger it to load
      if (routeMatch.status === 'success' && routeMatch.getIsInvalid() || routeMatch.status === 'error' || routeMatch.status === 'idle') {
        const maxAge = loaderOpts != null && loaderOpts.preload ? loaderOpts == null ? void 0 : loaderOpts.maxAge : undefined;
        await routeMatch.fetch({
          maxAge
        });
      }
    },
    fetch: async opts => {
      const loadId = '' + Date.now() + Math.random();
      routeMatch.__.latestId = loadId;
      const checkLatest = async () => {
        if (loadId !== routeMatch.__.latestId) {
          // warning(true, 'Data loader is out of date!')
          return new Promise(() => {});
        }
      };

      // If the match was in an error state, set it
      // to a loading state again. Otherwise, keep it
      // as loading or resolved
      if (routeMatch.status === 'idle') {
        routeMatch.status = 'loading';
      }

      // We started loading the route, so it's no longer invalid
      routeMatch.isInvalid = false;
      routeMatch.__.loadPromise = new Promise(async resolve => {
        // We are now fetching, even if it's in the background of a
        // resolved state
        routeMatch.isFetching = true;
        routeMatch.__.resolve = resolve;
        routeMatch.__.componentsPromise = (async () => {
          // then run all component and data loaders in parallel
          // For each component type, potentially load it asynchronously

          await Promise.all(componentTypes.map(async type => {
            var _routeMatch$__$type;
            const component = routeMatch.options[type];
            if ((_routeMatch$__$type = routeMatch.__[type]) != null && _routeMatch$__$type.preload) {
              routeMatch.__[type] = await router.options.loadComponent(component);
            }
          }));
        })();
        routeMatch.__.dataPromise = Promise.resolve().then(async () => {
          try {
            var _ref, _ref2, _opts$maxAge;
            if (routeMatch.options.loader) {
              const data = await router.loadMatchData(routeMatch);
              await checkLatest();
              routeMatch.routeLoaderData = replaceEqualDeep(routeMatch.routeLoaderData, data);
            }
            routeMatch.error = undefined;
            routeMatch.status = 'success';
            routeMatch.updatedAt = Date.now();
            routeMatch.invalidAt = routeMatch.updatedAt + ((_ref = (_ref2 = (_opts$maxAge = opts == null ? void 0 : opts.maxAge) != null ? _opts$maxAge : routeMatch.options.loaderMaxAge) != null ? _ref2 : router.options.defaultLoaderMaxAge) != null ? _ref : 0);
            return routeMatch.routeLoaderData;
          } catch (err) {
            await checkLatest();
            if (process.env.NODE_ENV !== 'production') {
              console.error(err);
            }
            routeMatch.error = err;
            routeMatch.status = 'error';
            routeMatch.updatedAt = Date.now();
            throw err;
          }
        });
        const after = async () => {
          await checkLatest();
          routeMatch.isFetching = false;
          delete routeMatch.__.loadPromise;
          routeMatch.__.notify();
        };
        try {
          await Promise.all([routeMatch.__.componentsPromise, routeMatch.__.dataPromise.catch(() => {})]);
          after();
        } catch (_unused) {
          after();
        }
      });
      await routeMatch.__.loadPromise;
      await checkLatest();
    }
  });
  if (!routeMatch.hasLoaders()) {
    routeMatch.status = 'success';
  }
  return routeMatch;
}

const defaultParseSearch = parseSearchWith(JSON.parse);
const defaultStringifySearch = stringifySearchWith(JSON.stringify);
function parseSearchWith(parser) {
  return searchStr => {
    if (searchStr.substring(0, 1) === '?') {
      searchStr = searchStr.substring(1);
    }
    let query = decode(searchStr);

    // Try to parse any query params that might be json
    for (let key in query) {
      const value = query[key];
      if (typeof value === 'string') {
        try {
          query[key] = parser(value);
        } catch (err) {
          //
        }
      }
    }
    return query;
  };
}
function stringifySearchWith(stringify) {
  return search => {
    search = _extends({}, search);
    if (search) {
      Object.keys(search).forEach(key => {
        const val = search[key];
        if (typeof val === 'undefined' || val === undefined) {
          delete search[key];
        } else if (val && typeof val === 'object' && val !== null) {
          try {
            search[key] = stringify(val);
          } catch (err) {
            // silent
          }
        }
      });
    }
    const searchStr = encode(search).toString();
    return searchStr ? "?" + searchStr : '';
  };
}

var _window$document;
// Detect if we're in the DOM
const isServer = typeof window === 'undefined' || !((_window$document = window.document) != null && _window$document.createElement);

// This is the default history object if none is defined
const createDefaultHistory = () => isServer ? createMemoryHistory() : createBrowserHistory();
function getInitialRouterState() {
  return {
    status: 'idle',
    latestLocation: null,
    currentLocation: null,
    currentMatches: [],
    actions: {},
    loaders: {},
    lastUpdated: Date.now(),
    isFetching: false,
    isPreloading: false
  };
}
function createRouter(userOptions) {
  var _userOptions$stringif, _userOptions$parseSea;
  const history = (userOptions == null ? void 0 : userOptions.history) || createDefaultHistory();
  const originalOptions = _extends({
    defaultLoaderGcMaxAge: 5 * 60 * 1000,
    defaultLoaderMaxAge: 0,
    defaultPreloadMaxAge: 2000,
    defaultPreloadDelay: 50,
    context: undefined
  }, userOptions, {
    stringifySearch: (_userOptions$stringif = userOptions == null ? void 0 : userOptions.stringifySearch) != null ? _userOptions$stringif : defaultStringifySearch,
    parseSearch: (_userOptions$parseSea = userOptions == null ? void 0 : userOptions.parseSearch) != null ? _userOptions$parseSea : defaultParseSearch
  });
  let router = {
    types: undefined,
    // public api
    history,
    options: originalOptions,
    listeners: [],
    // Resolved after construction
    basepath: '',
    routeTree: undefined,
    routesById: {},
    //
    resolveNavigation: () => {},
    matchCache: {},
    state: getInitialRouterState(),
    reset: () => {
      router.state = getInitialRouterState();
      router.notify();
    },
    startedLoadingAt: Date.now(),
    subscribe: listener => {
      router.listeners.push(listener);
      return () => {
        router.listeners = router.listeners.filter(x => x !== listener);
      };
    },
    getRoute: id => {
      return router.routesById[id];
    },
    notify: () => {
      const isFetching = router.state.status === 'loading' || router.state.currentMatches.some(d => d.isFetching);
      const isPreloading = Object.values(router.matchCache).some(d => d.match.isFetching && !router.state.currentMatches.find(dd => dd.matchId === d.match.matchId));
      if (router.state.isFetching !== isFetching || router.state.isPreloading !== isPreloading) {
        router.state = _extends({}, router.state, {
          isFetching,
          isPreloading
        });
      }
      cascadeLoaderData(router.state.currentMatches);
      router.listeners.forEach(listener => listener(router));
    },
    dehydrate: () => {
      return {
        state: _extends({}, pick(router.state, ['latestLocation', 'currentLocation', 'status', 'lastUpdated']), {
          currentMatches: router.state.currentMatches.map(match => pick(match, ['matchId', 'status', 'routeLoaderData', 'loaderData', 'isInvalid', 'invalidAt']))
        }),
        context: router.options.context
      };
    },
    hydrate: dehydratedState => {
      // Update the location
      router.state.latestLocation = dehydratedState.state.latestLocation;
      router.state.currentLocation = dehydratedState.state.currentLocation;

      // Update the context
      router.options.context = dehydratedState.context;

      // Match the routes
      const currentMatches = router.matchRoutes(router.state.latestLocation.pathname, {
        strictParseParams: true
      });
      currentMatches.forEach((match, index) => {
        const dehydratedMatch = dehydratedState.state.currentMatches[index];
        invariant(dehydratedMatch, 'Oh no! Dehydrated route matches did not match the active state of the router 😬');
        Object.assign(match, dehydratedMatch);
      });
      currentMatches.forEach(match => match.__.validate());
      router.state = _extends({}, router.state, dehydratedState, {
        currentMatches
      });
    },
    mount: () => {
      if (!router.state.currentMatches.length) {
        router.load();
      }
      const unsub = router.history.listen(event => {
        router.load(router.__.parseLocation(event.location, router.state.latestLocation));
      });

      // addEventListener does not exist in React Native, but window does
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!isServer && window.addEventListener) {
        // Listen to visibillitychange and focus
        window.addEventListener('visibilitychange', router.onFocus, false);
        window.addEventListener('focus', router.onFocus, false);
      }
      return () => {
        unsub();
        if (!isServer && window.removeEventListener) {
          // Be sure to unsubscribe if a new handler is set
          window.removeEventListener('visibilitychange', router.onFocus);
          window.removeEventListener('focus', router.onFocus);
        }
      };
    },
    onFocus: () => {
      router.load();
    },
    update: opts => {
      var _trimPath;
      const newHistory = (opts == null ? void 0 : opts.history) !== router.history;
      if (!router.state.latestLocation || newHistory) {
        if (opts != null && opts.history) {
          router.history = opts.history;
        }
        router.state.latestLocation = router.__.parseLocation(router.history.location);
        router.state.currentLocation = router.state.latestLocation;
      }
      Object.assign(router.options, opts);
      const {
        basepath,
        routeConfig
      } = router.options;
      router.basepath = "/" + ((_trimPath = trimPath(basepath != null ? basepath : '')) != null ? _trimPath : '');
      if (routeConfig) {
        router.routesById = {};
        router.routeTree = router.__.buildRouteTree(routeConfig);
      }
      return router;
    },
    cancelMatches: () => {
      var _router$state$pending;
      [...router.state.currentMatches, ...((_router$state$pending = router.state.pendingMatches) != null ? _router$state$pending : [])].forEach(match => {
        match.cancel();
      });
    },
    load: async next => {
      const id = Math.random();
      router.startedLoadingAt = id;
      if (next) {
        // Ingest the new location
        router.state.latestLocation = next;
      }

      // Cancel any pending matches
      router.cancelMatches();

      // Match the routes
      const matches = router.matchRoutes(router.state.latestLocation.pathname, {
        strictParseParams: true
      });
      if (typeof document !== 'undefined') {
        router.state = _extends({}, router.state, {
          status: 'loading',
          pendingMatches: matches,
          pendingLocation: router.state.latestLocation
        });
      } else {
        router.state = _extends({}, router.state, {
          status: 'loading',
          currentMatches: matches,
          currentLocation: router.state.latestLocation
        });
      }
      router.notify();

      // Load the matches
      try {
        await router.loadMatches(matches);
      } catch (err) {
        console.log(err);
        invariant(false, 'Matches failed to load due to error above ☝️. Navigation cancelled!');
      }
      if (router.startedLoadingAt !== id) {
        // Ignore side-effects of match loading
        return router.navigationPromise;
      }
      const previousMatches = router.state.currentMatches;
      const exiting = [],
        staying = [];
      previousMatches.forEach(d => {
        if (matches.find(dd => dd.matchId === d.matchId)) {
          staying.push(d);
        } else {
          exiting.push(d);
        }
      });
      const entering = matches.filter(d => {
        return !previousMatches.find(dd => dd.matchId === d.matchId);
      });
      const now = Date.now();
      exiting.forEach(d => {
        var _ref, _d$options$loaderGcMa, _ref2, _d$options$loaderMaxA;
        d.__.onExit == null ? void 0 : d.__.onExit({
          params: d.params,
          search: d.routeSearch
        });

        // Clear idle error states when match leaves
        if (d.status === 'error' && !d.isFetching) {
          d.status = 'idle';
          d.error = undefined;
        }
        const gc = Math.max((_ref = (_d$options$loaderGcMa = d.options.loaderGcMaxAge) != null ? _d$options$loaderGcMa : router.options.defaultLoaderGcMaxAge) != null ? _ref : 0, (_ref2 = (_d$options$loaderMaxA = d.options.loaderMaxAge) != null ? _d$options$loaderMaxA : router.options.defaultLoaderMaxAge) != null ? _ref2 : 0);
        if (gc > 0) {
          router.matchCache[d.matchId] = {
            gc: gc == Infinity ? Number.MAX_SAFE_INTEGER : now + gc,
            match: d
          };
        }
      });
      staying.forEach(d => {
        d.options.onTransition == null ? void 0 : d.options.onTransition({
          params: d.params,
          search: d.routeSearch
        });
      });
      entering.forEach(d => {
        d.__.onExit = d.options.onLoaded == null ? void 0 : d.options.onLoaded({
          params: d.params,
          search: d.search
        });
        delete router.matchCache[d.matchId];
      });
      if (router.startedLoadingAt !== id) {
        // Ignore side-effects of match loading
        return;
      }
      matches.forEach(match => {
        // Clear actions
        if (match.action) {
          match.action.current = undefined;
          match.action.submissions = [];
        }
      });
      router.state = _extends({}, router.state, {
        status: 'idle',
        currentLocation: router.state.latestLocation,
        currentMatches: matches,
        pendingLocation: undefined,
        pendingMatches: undefined
      });
      router.notify();
      router.resolveNavigation();
    },
    cleanMatchCache: () => {
      const now = Date.now();
      Object.keys(router.matchCache).forEach(matchId => {
        const entry = router.matchCache[matchId];

        // Don't remove loading matches
        if (entry.match.status === 'loading') {
          return;
        }

        // Do not remove successful matches that are still valid
        if (entry.gc > 0 && entry.gc > now) {
          return;
        }

        // Everything else gets removed
        delete router.matchCache[matchId];
      });
    },
    loadRoute: async function loadRoute(navigateOpts) {
      if (navigateOpts === void 0) {
        navigateOpts = router.state.latestLocation;
      }
      const next = router.buildNext(navigateOpts);
      const matches = router.matchRoutes(next.pathname, {
        strictParseParams: true
      });
      await router.loadMatches(matches);
      return matches;
    },
    preloadRoute: async function preloadRoute(navigateOpts, loaderOpts) {
      var _ref3, _ref4, _loaderOpts$maxAge, _ref5, _ref6, _loaderOpts$gcMaxAge;
      if (navigateOpts === void 0) {
        navigateOpts = router.state.latestLocation;
      }
      const next = router.buildNext(navigateOpts);
      const matches = router.matchRoutes(next.pathname, {
        strictParseParams: true
      });
      await router.loadMatches(matches, {
        preload: true,
        maxAge: (_ref3 = (_ref4 = (_loaderOpts$maxAge = loaderOpts.maxAge) != null ? _loaderOpts$maxAge : router.options.defaultPreloadMaxAge) != null ? _ref4 : router.options.defaultLoaderMaxAge) != null ? _ref3 : 0,
        gcMaxAge: (_ref5 = (_ref6 = (_loaderOpts$gcMaxAge = loaderOpts.gcMaxAge) != null ? _loaderOpts$gcMaxAge : router.options.defaultPreloadGcMaxAge) != null ? _ref6 : router.options.defaultLoaderGcMaxAge) != null ? _ref5 : 0
      });
      return matches;
    },
    matchRoutes: (pathname, opts) => {
      var _router$state$pending2;
      router.cleanMatchCache();
      const matches = [];
      if (!router.routeTree) {
        return matches;
      }
      const existingMatches = [...router.state.currentMatches, ...((_router$state$pending2 = router.state.pendingMatches) != null ? _router$state$pending2 : [])];
      const recurse = async routes => {
        var _parentMatch$params, _router$options$filte, _foundRoute$childRout;
        const parentMatch = last(matches);
        let params = (_parentMatch$params = parentMatch == null ? void 0 : parentMatch.params) != null ? _parentMatch$params : {};
        const filteredRoutes = (_router$options$filte = router.options.filterRoutes == null ? void 0 : router.options.filterRoutes(routes)) != null ? _router$options$filte : routes;
        let foundRoutes = [];
        const findMatchInRoutes = (parentRoutes, routes) => {
          routes.some(route => {
            var _route$childRoutes, _route$childRoutes2, _route$options$caseSe;
            if (!route.routePath && (_route$childRoutes = route.childRoutes) != null && _route$childRoutes.length) {
              return findMatchInRoutes([...foundRoutes, route], route.childRoutes);
            }
            const fuzzy = !!(route.routePath !== '/' || (_route$childRoutes2 = route.childRoutes) != null && _route$childRoutes2.length);
            const matchParams = matchPathname(router.basepath, pathname, {
              to: route.fullPath,
              fuzzy,
              caseSensitive: (_route$options$caseSe = route.options.caseSensitive) != null ? _route$options$caseSe : router.options.caseSensitive
            });

            // console.log(
            //   router.basepath,
            //   route.fullPath,
            //   fuzzy,
            //   pathname,
            //   matchParams,
            // )

            if (matchParams) {
              let parsedParams;
              try {
                var _route$options$parseP;
                parsedParams = (_route$options$parseP = route.options.parseParams == null ? void 0 : route.options.parseParams(matchParams)) != null ? _route$options$parseP : matchParams;
              } catch (err) {
                if (opts != null && opts.strictParseParams) {
                  throw err;
                }
              }
              params = _extends({}, params, parsedParams);
            }
            if (!!matchParams) {
              foundRoutes = [...parentRoutes, route];
            }
            return !!foundRoutes.length;
          });
          return !!foundRoutes.length;
        };
        findMatchInRoutes([], filteredRoutes);
        if (!foundRoutes.length) {
          return;
        }
        foundRoutes.forEach(foundRoute => {
          var _router$matchCache$ma;
          const interpolatedPath = interpolatePath(foundRoute.routePath, params);
          const matchId = interpolatePath(foundRoute.routeId, params, true);
          const match = existingMatches.find(d => d.matchId === matchId) || ((_router$matchCache$ma = router.matchCache[matchId]) == null ? void 0 : _router$matchCache$ma.match) || createRouteMatch(router, foundRoute, {
            parentMatch,
            matchId,
            params,
            pathname: joinPaths([router.basepath, interpolatedPath])
          });
          matches.push(match);
        });
        const foundRoute = last(foundRoutes);
        if ((_foundRoute$childRout = foundRoute.childRoutes) != null && _foundRoute$childRout.length) {
          recurse(foundRoute.childRoutes);
        }
      };
      recurse([router.routeTree]);
      cascadeLoaderData(matches);
      return matches;
    },
    loadMatches: async (resolvedMatches, loaderOpts) => {
      resolvedMatches.forEach(async match => {
        // Validate the match (loads search params etc)
        match.__.validate();
      });

      // Check each match middleware to see if the route can be accessed
      await Promise.all(resolvedMatches.map(async match => {
        try {
          await (match.options.beforeLoad == null ? void 0 : match.options.beforeLoad({
            router: router,
            match
          }));
        } catch (err) {
          if (!(loaderOpts != null && loaderOpts.preload)) {
            match.options.onLoadError == null ? void 0 : match.options.onLoadError(err);
          }
          throw err;
        }
      }));
      const matchPromises = resolvedMatches.map(async match => {
        var _search$__data;
        const search = match.search;
        if ((_search$__data = search.__data) != null && _search$__data.matchId && search.__data.matchId !== match.matchId) {
          return;
        }
        match.load(loaderOpts);
        if (match.status !== 'success' && match.__.loadPromise) {
          // Wait for the first sign of activity from the match
          await match.__.loadPromise;
        }
      });
      router.notify();
      await Promise.all(matchPromises);
    },
    loadMatchData: async routeMatch => {
      if (isServer || !router.options.useServerData) {
        var _await$routeMatch$opt;
        return (_await$routeMatch$opt = await (routeMatch.options.loader == null ? void 0 : routeMatch.options.loader({
          // parentLoaderPromise: routeMatch.parentMatch?.__.dataPromise,
          params: routeMatch.params,
          search: routeMatch.routeSearch,
          signal: routeMatch.__.abortController.signal
        }))) != null ? _await$routeMatch$opt : {};
      } else {
        const next = router.buildNext({
          to: '.',
          search: d => _extends({}, d != null ? d : {}, {
            __data: {
              matchId: routeMatch.matchId
            }
          })
        });

        // Refresh:
        // '/dashboard'
        // '/dashboard/invoices/'
        // '/dashboard/invoices/123'

        // New:
        // '/dashboard/invoices/456'

        // TODO: batch requests when possible

        const res = await fetch(next.href, {
          method: 'GET'
          // signal: routeMatch.__.abortController.signal,
        });

        if (res.ok) {
          return res.json();
        }
        throw new Error('Failed to fetch match data');
      }
    },
    invalidateRoute: opts => {
      var _router$state$pending3;
      const next = router.buildNext(opts);
      const unloadedMatchIds = router.matchRoutes(next.pathname).map(d => d.matchId);
      [...router.state.currentMatches, ...((_router$state$pending3 = router.state.pendingMatches) != null ? _router$state$pending3 : [])].forEach(match => {
        if (unloadedMatchIds.includes(match.matchId)) {
          match.invalidate();
        }
      });
    },
    reload: () => router.__.navigate({
      fromCurrent: true,
      replace: true,
      search: true
    }),
    resolvePath: (from, path) => {
      return resolvePath(router.basepath, from, cleanPath(path));
    },
    matchRoute: (location, opts) => {
      var _location$from;
      // const location = router.buildNext(opts)

      location = _extends({}, location, {
        to: location.to ? router.resolvePath((_location$from = location.from) != null ? _location$from : '', location.to) : undefined
      });
      const next = router.buildNext(location);
      if (opts != null && opts.pending) {
        if (!router.state.pendingLocation) {
          return false;
        }
        return !!matchPathname(router.basepath, router.state.pendingLocation.pathname, _extends({}, opts, {
          to: next.pathname
        }));
      }
      return !!matchPathname(router.basepath, router.state.currentLocation.pathname, _extends({}, opts, {
        to: next.pathname
      }));
    },
    navigate: async _ref7 => {
      let {
        from,
        to = '.',
        search,
        hash,
        replace,
        params
      } = _ref7;
      // If this link simply reloads the current route,
      // make sure it has a new key so it will trigger a data refresh

      // If this `to` is a valid external URL, return
      // null for LinkUtils
      const toString = String(to);
      const fromString = String(from);
      let isExternal;
      try {
        new URL("" + toString);
        isExternal = true;
      } catch (e) {}
      invariant(!isExternal, 'Attempting to navigate to external url with router.navigate!');
      return router.__.navigate({
        from: fromString,
        to: toString,
        search,
        hash,
        replace,
        params
      });
    },
    buildLink: _ref8 => {
      var _preload, _ref9;
      let {
        from,
        to = '.',
        search,
        params,
        hash,
        target,
        replace,
        activeOptions,
        preload,
        preloadMaxAge: userPreloadMaxAge,
        preloadGcMaxAge: userPreloadGcMaxAge,
        preloadDelay: userPreloadDelay,
        disabled
      } = _ref8;
      // If this link simply reloads the current route,
      // make sure it has a new key so it will trigger a data refresh

      // If this `to` is a valid external URL, return
      // null for LinkUtils

      try {
        new URL("" + to);
        return {
          type: 'external',
          href: to
        };
      } catch (e) {}
      const nextOpts = {
        from,
        to,
        search,
        params,
        hash,
        replace
      };
      const next = router.buildNext(nextOpts);
      preload = (_preload = preload) != null ? _preload : router.options.defaultPreload;
      const preloadDelay = (_ref9 = userPreloadDelay != null ? userPreloadDelay : router.options.defaultPreloadDelay) != null ? _ref9 : 0;

      // Compare path/hash for matches
      const pathIsEqual = router.state.currentLocation.pathname === next.pathname;
      const currentPathSplit = router.state.currentLocation.pathname.split('/');
      const nextPathSplit = next.pathname.split('/');
      const pathIsFuzzyEqual = nextPathSplit.every((d, i) => d === currentPathSplit[i]);
      const hashIsEqual = router.state.currentLocation.hash === next.hash;
      // Combine the matches based on user options
      const pathTest = activeOptions != null && activeOptions.exact ? pathIsEqual : pathIsFuzzyEqual;
      const hashTest = activeOptions != null && activeOptions.includeHash ? hashIsEqual : true;

      // The final "active" test
      const isActive = pathTest && hashTest;

      // The click handler
      const handleClick = e => {
        if (!disabled && !isCtrlEvent(e) && !e.defaultPrevented && (!target || target === '_self') && e.button === 0) {
          e.preventDefault();
          if (pathIsEqual && !search && !hash) {
            router.invalidateRoute(nextOpts);
          }

          // All is well? Navigate!)
          router.__.navigate(nextOpts);
        }
      };

      // The click handler
      const handleFocus = e => {
        if (preload) {
          router.preloadRoute(nextOpts, {
            maxAge: userPreloadMaxAge,
            gcMaxAge: userPreloadGcMaxAge
          }).catch(err => {
            console.log(err);
            console.warn('Error preloading route! ☝️');
          });
        }
      };
      const handleEnter = e => {
        const target = e.target || {};
        if (preload) {
          if (target.preloadTimeout) {
            return;
          }
          target.preloadTimeout = setTimeout(() => {
            target.preloadTimeout = null;
            router.preloadRoute(nextOpts, {
              maxAge: userPreloadMaxAge,
              gcMaxAge: userPreloadGcMaxAge
            }).catch(err => {
              console.log(err);
              console.warn('Error preloading route! ☝️');
            });
          }, preloadDelay);
        }
      };
      const handleLeave = e => {
        const target = e.target || {};
        if (target.preloadTimeout) {
          clearTimeout(target.preloadTimeout);
          target.preloadTimeout = null;
        }
      };
      return {
        type: 'internal',
        next,
        handleFocus,
        handleClick,
        handleEnter,
        handleLeave,
        isActive,
        disabled
      };
    },
    buildNext: opts => {
      const next = router.__.buildLocation(opts);
      const matches = router.matchRoutes(next.pathname);
      const __preSearchFilters = matches.map(match => {
        var _match$options$preSea;
        return (_match$options$preSea = match.options.preSearchFilters) != null ? _match$options$preSea : [];
      }).flat().filter(Boolean);
      const __postSearchFilters = matches.map(match => {
        var _match$options$postSe;
        return (_match$options$postSe = match.options.postSearchFilters) != null ? _match$options$postSe : [];
      }).flat().filter(Boolean);
      return router.__.buildLocation(_extends({}, opts, {
        __preSearchFilters,
        __postSearchFilters
      }));
    },
    __: {
      buildRouteTree: rootRouteConfig => {
        const recurseRoutes = (routeConfigs, parent) => {
          return routeConfigs.map(routeConfig => {
            const routeOptions = routeConfig.options;
            const route = createRoute(routeConfig, routeOptions, parent, router);
            const existingRoute = router.routesById[route.routeId];
            if (existingRoute) {
              if (process.env.NODE_ENV !== 'production') {
                console.warn("Duplicate routes found with id: " + String(route.routeId), router.routesById, route);
              }
              throw new Error();
            }
            router.routesById[route.routeId] = route;
            const children = routeConfig.children;
            route.childRoutes = children != null && children.length ? recurseRoutes(children, route) : undefined;
            return route;
          });
        };
        const routes = recurseRoutes([rootRouteConfig]);
        return routes[0];
      },
      parseLocation: (location, previousLocation) => {
        var _location$hash$split$;
        const parsedSearch = router.options.parseSearch(location.search);
        return {
          pathname: location.pathname,
          searchStr: location.search,
          search: replaceEqualDeep(previousLocation == null ? void 0 : previousLocation.search, parsedSearch),
          hash: (_location$hash$split$ = location.hash.split('#').reverse()[0]) != null ? _location$hash$split$ : '',
          href: "" + location.pathname + location.search + location.hash,
          state: location.state,
          key: location.key
        };
      },
      navigate: location => {
        const next = router.buildNext(location);
        return router.__.commitLocation(next, location.replace);
      },
      buildLocation: function buildLocation(dest) {
        var _dest$from, _router$basepath, _dest$to, _last, _dest$params, _dest$__preSearchFilt, _functionalUpdate, _dest$__preSearchFilt2, _dest$__postSearchFil;
        if (dest === void 0) {
          dest = {};
        }
        const fromPathname = dest.fromCurrent ? router.state.latestLocation.pathname : (_dest$from = dest.from) != null ? _dest$from : router.state.latestLocation.pathname;
        let pathname = resolvePath((_router$basepath = router.basepath) != null ? _router$basepath : '/', fromPathname, "" + ((_dest$to = dest.to) != null ? _dest$to : '.'));
        const fromMatches = router.matchRoutes(router.state.latestLocation.pathname, {
          strictParseParams: true
        });
        const toMatches = router.matchRoutes(pathname);
        const prevParams = _extends({}, (_last = last(fromMatches)) == null ? void 0 : _last.params);
        let nextParams = ((_dest$params = dest.params) != null ? _dest$params : true) === true ? prevParams : functionalUpdate(dest.params, prevParams);
        if (nextParams) {
          toMatches.map(d => d.options.stringifyParams).filter(Boolean).forEach(fn => {
            Object.assign({}, nextParams, fn(nextParams));
          });
        }
        pathname = interpolatePath(pathname, nextParams != null ? nextParams : {});

        // Pre filters first
        const preFilteredSearch = (_dest$__preSearchFilt = dest.__preSearchFilters) != null && _dest$__preSearchFilt.length ? dest.__preSearchFilters.reduce((prev, next) => next(prev), router.state.latestLocation.search) : router.state.latestLocation.search;

        // Then the link/navigate function
        const destSearch = dest.search === true ? preFilteredSearch // Preserve resolvedFrom true
        : dest.search ? (_functionalUpdate = functionalUpdate(dest.search, preFilteredSearch)) != null ? _functionalUpdate : {} // Updater
        : (_dest$__preSearchFilt2 = dest.__preSearchFilters) != null && _dest$__preSearchFilt2.length ? preFilteredSearch // Preserve resolvedFrom filters
        : {};

        // Then post filters
        const postFilteredSearch = (_dest$__postSearchFil = dest.__postSearchFilters) != null && _dest$__postSearchFil.length ? dest.__postSearchFilters.reduce((prev, next) => next(prev), destSearch) : destSearch;
        const search = replaceEqualDeep(router.state.latestLocation.search, postFilteredSearch);
        const searchStr = router.options.stringifySearch(search);
        let hash = dest.hash === true ? router.state.latestLocation.hash : functionalUpdate(dest.hash, router.state.latestLocation.hash);
        hash = hash ? "#" + hash : '';
        return {
          pathname,
          search,
          searchStr,
          state: router.state.latestLocation.state,
          hash,
          href: "" + pathname + searchStr + hash,
          key: dest.key
        };
      },
      commitLocation: (next, replace) => {
        const id = '' + Date.now() + Math.random();
        if (router.navigateTimeout) clearTimeout(router.navigateTimeout);
        let nextAction = 'replace';
        if (!replace) {
          nextAction = 'push';
        }
        const isSameUrl = router.__.parseLocation(history.location).href === next.href;
        if (isSameUrl && !next.key) {
          nextAction = 'replace';
        }
        if (nextAction === 'replace') {
          history.replace({
            pathname: next.pathname,
            hash: next.hash,
            search: next.searchStr
          }, _extends({
            id
          }, next.state));
        } else {
          history.push({
            pathname: next.pathname,
            hash: next.hash,
            search: next.searchStr
          }, {
            id
          });
        }
        router.navigationPromise = new Promise(resolve => {
          const previousNavigationResolve = router.resolveNavigation;
          router.resolveNavigation = () => {
            previousNavigationResolve();
            resolve();
            delete router.navigationPromise;
          };
        });
        return router.navigationPromise;
      }
    }
  };
  router.update(userOptions);

  // Allow frameworks to hook into the router creation
  router.options.createRouter == null ? void 0 : router.options.createRouter(router);
  return router;
}
function isCtrlEvent(e) {
  return !!(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey);
}
function cascadeLoaderData(matches) {
  matches.forEach((match, index) => {
    const parent = matches[index - 1];
    if (parent) {
      match.loaderData = replaceEqualDeep(match.loaderData, _extends({}, parent.loaderData, match.routeLoaderData));
    }
  });
}

export { cleanPath, createRoute, createRouteConfig, createRouteMatch, createRouter, decode, defaultParseSearch, defaultStringifySearch, encode, functionalUpdate, interpolatePath, joinPaths, last, matchByPath, matchPathname, parsePathname, parseSearchWith, pick, replaceEqualDeep, resolvePath, rootRouteId, stringifySearchWith, trimPath, trimPathLeft, trimPathRight, warning };
//# sourceMappingURL=index.js.map
